import isEqual from 'lodash/isEqual';
import isNumber from 'lodash/isNumber';
import round from 'lodash/round';

import {
  CellRendererFunc,
  ColumnGroup,
  CommonDataTableFields,
  GroupSortableColumnsDictionary,
  TableColumn,
  TableColumnType,
  TableDataType,
} from 'common/components/data-table/types';
import {PropertyType} from 'common/report/constants';
import {OVERALL_PROP} from 'common/report/funnels/models/constants';
import {
  BasicFunnelStep,
  FunnelBookmark,
  FunnelSegment,
  FunnelStep,
  PValue,
  SelectedColumns,
  TableMetrics,
  TableNonToggleableMetrics,
  TableToggleableMetrics,
} from 'common/report/funnels/models/types';
import {getTableColumnSpec, LIFT_PILL_EXTRA_WIDTH} from 'common/report/funnels/util/table-width';
import {formatPercent, getSegmentColumnSpecs} from 'common/report/funnels/util';
import {commaizeNumber, formatMetricNumber} from 'common/util';
import {formatDuration, MS_BY_UNIT} from 'common/util/date';
import {LiftType} from 'common/report/util/lift/types';
import {LiftComparison} from 'common/widgets/lift-comparison-menu/types';
import {FullWidthReport} from 'common/widgets/fullwidth-report';
import {DEFAULT_LIFT_COLUMN_WIDTH, GROUP_HEADER_MIN_WIDTH} from 'common/components/data-table/constants';

import {getCellStatSigRenderer, liftCellRenderer, StatSigClickHandler} from './cell-renderers';
import {FunnelTableRow, StatSigDisplay, StatSigDisplayStatusColor} from './types';

function overallConversionRatio(step: BasicFunnelStep | FunnelStep): number {
  return round(step.overall_conv_ratio * 100, 2);
}

function stepConversionRatio(step: BasicFunnelStep | FunnelStep): number {
  return round(step.step_conv_ratio * 100, 2);
}

function countTransform(row: FunnelTableRow, field: string) {
  const value = row[field];
  return value >= 10 ** 7 ? `${formatMetricNumber(value)}` : `${commaizeNumber(value)}`;
}

function pctTransform(row: FunnelTableRow, field: string) {
  return formatPercent(row[field], 2);
}

function fixedPointPercentTransform(row: FunnelTableRow, field: string) {
  return formatPercent(row[field], 2, true, true);
}

export function formatStepConversionDuration(seconds) {
  if (!isNumber(seconds)) {
    return `-`;
  }
  const duration =
    seconds < 500 ? {value: round(seconds, 0), unit: `s`} : formatDuration(seconds * MS_BY_UNIT.second, 1, true);
  return `${duration.value}${duration.unit}`;
}

export function getStatisticalSignificance(pValue: PValue): StatSigDisplay {
  const {confidence, is_interesting: isInteresting, insufficient_samples: insufficientSamples} = pValue;
  if (insufficientSamples) {
    return {confidence: `-`};
  }
  return {
    confidence: confidence > 0.99 ? `0.99` : confidence.toFixed(2),
    status: isInteresting ? StatSigDisplayStatusColor.Green : StatSigDisplayStatusColor.Red,
    label: isInteresting ? `Statistically significant` : `Not statistically significant`,
  };
}

function stepStatsLabelTransform(row: FunnelTableRow, field: string): string {
  return formatStepConversionDuration(row[field]);
}

export function statSigSortTransform(row: FunnelTableRow): number {
  if (!row.statSig) {
    return null;
  }
  const {status, confidence} = row.statSig;
  const {count} = row.displayInfo.steps[0];
  const totalFunnelSteps = row.displayInfo.steps;
  const {conversionRatio} = row.displayInfo.steps[totalFunnelSteps.length - 1];
  if (confidence === ``) {
    return Number.NEGATIVE_INFINITY;
  } else if (status === StatSigDisplayStatusColor.Green) {
    return -1 * conversionRatio;
  } else if (status === StatSigDisplayStatusColor.Red) {
    return 1.0 / count;
  } else {
    return 1 + 1.0 / count;
  }
}

export const FIELD_STAT_SIG = `statSig`;
export const DEFAULT_SORT_ORDER = 1;
export const DEFAULT_SORT_FIELD = FIELD_STAT_SIG;

const DATA_TABLE_LIFT_PREFIX = `lift-`;
const DATA_TABLE_LIFT_EXTRA_PREFIX = `liftExtra-`;

function getDataTableColumnIdentifiers(
  step,
  index: number,
  liftType?: LiftType,
  liftStep?: FunnelStep,
  baselineStep?: FunnelStep,
) {
  const segmentDict = {};

  const stepCountKey = `step-${index}`;
  const stepPct = `step-${index}-pct`;
  const stepOverallPct = `step-${index}-overall-pct`;
  const statisticsOverall = `statistics-overall-${index}`;
  const statisticsAvg = `statistics-${index}`;

  segmentDict[stepCountKey] = step.count;
  segmentDict[stepPct] = step.step_conv_ratio;
  segmentDict[stepOverallPct] = step.overall_conv_ratio;
  segmentDict[statisticsOverall] = step.avg_time_from_start;
  segmentDict[statisticsAvg] = step.avg_time;

  if (liftType) {
    segmentDict[DATA_TABLE_LIFT_PREFIX + stepCountKey] = liftStep.count;
    segmentDict[DATA_TABLE_LIFT_PREFIX + stepPct] = liftStep.step_conv_ratio;
    segmentDict[DATA_TABLE_LIFT_PREFIX + stepOverallPct] = liftStep.overall_conv_ratio;
    segmentDict[DATA_TABLE_LIFT_PREFIX + statisticsOverall] = liftStep.avg_time_from_start;
    segmentDict[DATA_TABLE_LIFT_PREFIX + statisticsAvg] = liftStep.avg_time;
    segmentDict[DATA_TABLE_LIFT_EXTRA_PREFIX + stepCountKey] = baselineStep?.count || 0;
    segmentDict[DATA_TABLE_LIFT_EXTRA_PREFIX + stepPct] = baselineStep?.step_conv_ratio || 0;
    segmentDict[DATA_TABLE_LIFT_EXTRA_PREFIX + stepOverallPct] = baselineStep?.overall_conv_ratio || 0;
    segmentDict[DATA_TABLE_LIFT_EXTRA_PREFIX + statisticsOverall] = baselineStep?.avg_time_from_start || 0;
    segmentDict[DATA_TABLE_LIFT_EXTRA_PREFIX + statisticsAvg] = baselineStep?.avg_time || 0;
  }

  return segmentDict;
}

function getLabelTransform(
  tableIdentifier: TableMetrics,
  isSegmented: boolean,
): (row: FunnelTableRow, field: string) => string | number {
  const percentTransform = isSegmented ? fixedPointPercentTransform : pctTransform;

  switch (tableIdentifier) {
    case TableToggleableMetrics.ConvFirstStep:
    case TableToggleableMetrics.ConvPrevStep:
    case TableNonToggleableMetrics.OverallConv:
      return percentTransform;

    case TableToggleableMetrics.Count:
      return countTransform;

    case TableToggleableMetrics.TimeFirstStep:
    case TableToggleableMetrics.TimePrevStep:
      return stepStatsLabelTransform;

    case TableToggleableMetrics.StatSig:
    case TableNonToggleableMetrics.Segment:
    case TableNonToggleableMetrics.NoMetrics:
      return (row, field) => row[field];

    default:
      console.error(
        `Unrecognized column identifier for label transform: ${tableIdentifier} is not handled. Defaulting to unformatted label.`,
      );
      return (row, field) => row[field];
  }
}

/** Columns in step and step metric chart data tables that can be group sorted.
 * Key is the field that is group sortable, value is column fields to add (at least sortSelectTitle) */
export const GroupSortableStepColumns: GroupSortableColumnsDictionary = {
  'step-0': {sortSelectTitle: `Top of Funnel`, tooltip: `Number who performed this step\n(Top of Funnel)`},
  overall: {sortSelectTitle: `Total Conv.`},
  [FIELD_STAT_SIG]: {sortSelectTitle: `Sig.`},
};

// eslint-disable-next-line sonarjs/cognitive-complexity
export function getColumnGroups({
  bookmark,
  onStatSigClick,
  stepNames,
  segmentTypes,
  shouldShowStatSig,
  selectedColumns,
  tableWidth,
  showStepNamesInGroupHeaders,
  liftType,
}: {
  bookmark: FunnelBookmark;
  onStatSigClick: StatSigClickHandler;
  stepNames: string[];
  segmentTypes: PropertyType[];
  shouldShowStatSig: boolean;
  selectedColumns: SelectedColumns;
  tableWidth?: number;
  showStepNamesInGroupHeaders: boolean;
  liftType?: LiftType;
}): ColumnGroup[] {
  const renamedSegments = getSegmentColumnSpecs(bookmark).map(({title}) => title);
  const isSegmented = Boolean(renamedSegments.length);

  const canUseResizingLayoutVal = FullWidthReport.canUseResizingLayout();
  // TODO: Remove width computation logic after resizing layout is shipped
  const spec = getTableColumnSpec(
    selectedColumns,
    renamedSegments.length,
    stepNames.length,
    shouldShowStatSig,
    tableWidth,
  );
  const groups: TableColumn[][] = spec.map((columnGroup): TableColumn[] =>
    columnGroup.map(
      (column, idx): TableColumn => {
        const labelTransform = getLabelTransform(column.identifier, isSegmented);

        let cellRenderer: CellRendererFunc;
        if (liftType) {
          cellRenderer = liftCellRenderer(liftType, labelTransform);
        }

        let initialWidth: number;
        if (canUseResizingLayoutVal) {
          if (liftType && column.identifier !== TableToggleableMetrics.StatSig) {
            initialWidth = DEFAULT_LIFT_COLUMN_WIDTH;
          }
        } else {
          initialWidth = column.width;
          if (liftType && column.identifier !== TableToggleableMetrics.StatSig) {
            initialWidth += LIFT_PILL_EXTRA_WIDTH;
          }
        }

        const commonProps = {
          isSortable: true,
          fixedWidth: !showStepNamesInGroupHeaders,
          last: idx === columnGroup.length - 1,
          labelTransform,
          ...(Number.isFinite(initialWidth) ? {initialWidth} : {}),
          ...(cellRenderer ? {cellRenderer} : {}),
        };

        switch (column.identifier) {
          case TableNonToggleableMetrics.Segment:
            if (isSegmented) {
              const field = `${CommonDataTableFields.SegmentPrefix}${column.segmentIdx}`;
              return {
                ...commonProps,
                field,
                liftField: DATA_TABLE_LIFT_PREFIX + field,
                liftExtra: DATA_TABLE_LIFT_EXTRA_PREFIX + field,
                type: TableColumnType.Segment,
                contentType: segmentTypes[column.segmentIdx],
                title: renamedSegments[column.segmentIdx],
                sticky: true,
              };
            } else {
              const field = `${CommonDataTableFields.SegmentPrefix}0`;
              return {
                ...commonProps,
                field,
                liftField: DATA_TABLE_LIFT_PREFIX + field,
                liftExtra: DATA_TABLE_LIFT_EXTRA_PREFIX + field,
                type: TableColumnType.Segment,
                contentType: segmentTypes[column.segmentIdx],
                title: `Property`,
                sticky: true,
              };
            }
          case TableNonToggleableMetrics.OverallConv: {
            const field = `overall`;
            return {
              ...commonProps,
              field,
              liftField: DATA_TABLE_LIFT_PREFIX + field,
              liftExtra: DATA_TABLE_LIFT_EXTRA_PREFIX + field,
              type: TableColumnType.Data,
              contentType: TableDataType.Number,
              resizeMinWidth: 75,
              sticky: true,
              title: `Total Conv.`,
              tooltip: `Percent who converted all steps`,
              ...GroupSortableStepColumns[`overall`],
            };
          }

          case TableToggleableMetrics.StatSig:
            return {
              ...commonProps,
              field: FIELD_STAT_SIG,
              type: TableColumnType.Data,
              contentType: TableDataType.Number,
              cellRenderer: getCellStatSigRenderer(onStatSigClick),
              sortTransform: statSigSortTransform,
              sticky: true,
              title: `Sig.`,
              tooltip: `Statistical significance`,
              ...GroupSortableStepColumns[FIELD_STAT_SIG],
            };
          case TableToggleableMetrics.Count: {
            const field = `step-${column.stepIdx}`;
            return {
              ...commonProps,
              field,
              liftField: DATA_TABLE_LIFT_PREFIX + field,
              liftExtra: DATA_TABLE_LIFT_EXTRA_PREFIX + field,
              type: TableColumnType.Data,
              contentType: TableDataType.Number,
              tooltip: `Number who performed this step`,
              icon: `value-absolute`,
              ...(field in GroupSortableStepColumns ? GroupSortableStepColumns[field] : {}),
              ...(column.stepIdx === 0 && canUseResizingLayoutVal ? {initialWidth: GROUP_HEADER_MIN_WIDTH} : {}),
            };
          }
          case TableToggleableMetrics.ConvPrevStep: {
            const field = `step-${column.stepIdx}-pct`;
            return {
              ...commonProps,
              field,
              liftField: DATA_TABLE_LIFT_PREFIX + field,
              liftExtra: DATA_TABLE_LIFT_EXTRA_PREFIX + field,
              type: TableColumnType.Data,
              contentType: TableDataType.Number,
              tooltip: `Conversion from previous step`,
              icon: `conversion-prev-step`,
            };
          }
          case TableToggleableMetrics.ConvFirstStep: {
            const field = `step-${column.stepIdx}-overall-pct`;
            return {
              ...commonProps,
              field,
              liftField: DATA_TABLE_LIFT_PREFIX + field,
              liftExtra: DATA_TABLE_LIFT_EXTRA_PREFIX + field,
              type: TableColumnType.Data,
              contentType: TableDataType.Number,
              tooltip: `Conversion from first step`,
              icon: `conversion-first-step`,
            };
          }
          case TableToggleableMetrics.TimePrevStep: {
            const field = `statistics-${column.stepIdx}`;
            return {
              ...commonProps,
              field,
              liftField: DATA_TABLE_LIFT_PREFIX + field,
              liftExtra: DATA_TABLE_LIFT_EXTRA_PREFIX + field,
              type: TableColumnType.Data,
              contentType: TableDataType.Number,
              tooltip: `Average time from previous step`,
              icon: `time-prev-step`,
            };
          }
          case TableToggleableMetrics.TimeFirstStep: {
            const field = `statistics-overall-${column.stepIdx}`;
            return {
              ...commonProps,
              field,
              liftField: DATA_TABLE_LIFT_PREFIX + field,
              liftExtra: DATA_TABLE_LIFT_EXTRA_PREFIX + field,
              type: TableColumnType.Data,
              contentType: TableDataType.Number,
              tooltip: `Average time from first step`,
              icon: `time-first-step`,
            };
          }
          case TableNonToggleableMetrics.NoMetrics:
          default:
            return {
              ...commonProps,
              field: `unselected`,
              type: TableColumnType.Data,
              contentType: TableDataType.Number,
              tooltip: `Add metrics to view them here`,
              title: `No metrics`,
              isSortable: false,
            };
        }
      },
    ),
  );

  if (showStepNamesInGroupHeaders) {
    const groupsWithHeaders = groups
      .slice(-stepNames.length)
      .map((columns, idx) => ({columns, title: stepNames[idx], funnelStepNumber: idx + 1}));
    const stickyGroups = groups.slice(0, groups.length - groupsWithHeaders.length).map((columns) => ({columns}));

    return [...stickyGroups, ...groupsWithHeaders];
  }

  return groups.map((columns) => ({columns}));
}

function shouldShowPValues(segmentSteps: FunnelSegment[]): boolean {
  return !!segmentSteps && segmentSteps.length > 0;
}

function getFunnelLiftSegment({
  segmentIdx,
  stepIndex,
  liftComparison,
  liftSegmentSteps,
  compareSegmentSteps,
  segmentSteps,
}: {
  segmentIdx: number;
  stepIndex: number;
  liftComparison: LiftComparison;
  segmentSteps: FunnelSegment[];
  liftSegmentSteps: FunnelSegment[];
  compareSegmentSteps: FunnelSegment[];
}): {
  liftStep: FunnelStep;
  baselineStep?: FunnelStep;
} {
  switch (liftComparison.type) {
    case LiftType.Overall:
      // Not supported for steps chart
      return null;
    case LiftType.Segment: {
      const segmentProps = segmentSteps[segmentIdx].props.map((prop) => prop.prop);
      const baselineSegment = getBaselineSegment({
        funnelSegments: segmentSteps,
        segmentProps,
        groupValue: liftComparison.groupValue,
      });
      return {
        liftStep: liftSegmentSteps[segmentIdx].steps[stepIndex],
        baselineStep: baselineSegment ? baselineSegment.steps[stepIndex] : null,
      };
    }
    case LiftType.Time:
      return {
        liftStep: liftSegmentSteps[segmentIdx].steps[stepIndex],
        baselineStep: compareSegmentSteps[segmentIdx].steps[stepIndex],
      };
    default:
      return null;
  }
}

export function getBaselineSegment({
  funnelSegments,
  segmentProps,
  groupValue,
}: {
  funnelSegments: FunnelSegment[];
  segmentProps: string[];
  groupValue: string;
}) {
  return funnelSegments.find((segment) => {
    const props = segment.props;
    for (let i = 0; i < props.length - 1; i++) {
      if (props[i].prop !== segmentProps[i]) {
        return false;
      }
    }
    return props[props.length - 1].prop === groupValue;
  });
}

// TODO opslead
// eslint-disable-next-line sonarjs/cognitive-complexity
export function getRows({
  selected,
  controlGroup,
  segmentSteps,
  shouldShowStatSig = true,
  shouldFilterForSelected = false,
  liftComparison,
  liftSegmentSteps = null,
  compareSegmentSteps = null,
}: {
  selected: Map<string, string>;
  segmentSteps: FunnelSegment[];
  controlGroup: string[];
  shouldShowStatSig?: boolean;
  shouldFilterForSelected?: boolean;
  liftComparison?: LiftComparison;
  liftSegmentSteps?: FunnelSegment[] | null;
  compareSegmentSteps?: FunnelSegment[] | null;
}): FunnelTableRow[] {
  if (!segmentSteps.length) {
    return [];
  }
  const displayPValues = shouldShowStatSig && shouldShowPValues(segmentSteps);

  const totalFunnelSteps = segmentSteps[0].steps.length;
  const segmentRows = segmentSteps.flatMap((segment, segmentIdx) => {
    if (shouldFilterForSelected && !selected.has(segment.prop)) {
      return [];
    }
    const averageTimeBetweenSteps = [];
    const infoSteps = [];
    const isOverall = segment.prop === OVERALL_PROP;
    segment.steps.forEach((step) => {
      averageTimeBetweenSteps.push(step.avg_time);
      infoSteps.push({
        count: step.count,
        conversionRatio: isOverall ? overallConversionRatio(step) : stepConversionRatio(step),
      });
    });

    const prop = segment.prop;

    const isControlGroup = controlGroup?.length
      ? isEqual(
          controlGroup,
          segment.props.map((p) => p.prop),
        )
      : isOverall;

    let liftOverallSegments = {};
    if (liftComparison) {
      const liftSegmentForOverall = getFunnelLiftSegment({
        segmentIdx,
        stepIndex: segment.steps.length - 1,
        liftComparison,
        segmentSteps,
        liftSegmentSteps,
        compareSegmentSteps,
      });
      liftOverallSegments = {
        [DATA_TABLE_LIFT_PREFIX + `overall`]: liftSegmentForOverall.liftStep.overall_conv_ratio,
        [DATA_TABLE_LIFT_EXTRA_PREFIX + `overall`]: liftSegmentForOverall.baselineStep?.overall_conv_ratio || 0,
      };
    }

    const segmentDict: FunnelTableRow = {
      // TODO: Duplicate of prop in FunnelTableRow. Dedupe
      identifier: prop,
      prop,
      props: segment.props,
      overall: segment.steps[segment.steps.length - 1].overall_conv_ratio,
      ...liftOverallSegments,
      isSelected: selected.has(segment.prop),
      isControlGroup,
      displayInfo: {
        steps: infoSteps,
        averageTimeBetweenSteps,
      },
    };

    if (segmentDict.isSelected) {
      segmentDict.checkedColor = selected.get(segment.prop);
    }

    segment.props.forEach((segmentId, index) => {
      segmentDict[`${CommonDataTableFields.SegmentPrefix}${index}`] = segmentId.propName ?? segmentId.prop;
    });

    segment.steps.forEach((step, stepIndex) => {
      const liftData = liftComparison
        ? getFunnelLiftSegment({
            segmentIdx,
            stepIndex,
            liftComparison,
            segmentSteps,
            liftSegmentSteps,
            compareSegmentSteps,
          })
        : null;
      const segmentDataKeys = getDataTableColumnIdentifiers(
        step,
        stepIndex,
        liftComparison?.type,
        liftData?.liftStep,
        liftData?.baselineStep,
      );
      Object.assign(segmentDict, segmentDataKeys);
    });

    if (isOverall) {
      segmentDict.statSig = {confidence: ``};
    } else if (displayPValues && !isControlGroup) {
      const lastStep = segment.steps[totalFunnelSteps - 1];

      // @ts-ignore - TODO: Property 'p_value' does not exist on type 'BasicFunnelStep | FunnelStep'
      segmentDict.statSig = getStatisticalSignificance(lastStep.p_value);
    }
    return segmentDict;
  });

  // Move Overall row to first position
  const overallIndex = segmentRows.findIndex((row) => row.prop === OVERALL_PROP);
  if (overallIndex !== -1) {
    const overallRow = segmentRows.splice(overallIndex, 1)[0];
    segmentRows.splice(0, 0, overallRow);
  }

  // Move control group row to be the first position after Overall
  const controlGroupIndex = segmentRows.findIndex((row) => row.isControlGroup);
  const hasOverall = overallIndex !== -1;
  if (controlGroupIndex !== -1 && controlGroupIndex !== 0) {
    const controlGroupRow = segmentRows.splice(controlGroupIndex, 1)[0];
    segmentRows.splice(hasOverall ? 1 : 0, 0, controlGroupRow);
  }

  return segmentRows;
}
